import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Col, Row } from 'antd';
import { CollapseBlock } from '../../../../common/index.jsx';
import { useTranslation } from 'react-i18next';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import FormDate from '../../../../antd/form/FormDate/FormDate.jsx';
import { required } from '../../../../antd/form/FormValidation.js';
import {
  DATETIME_FORMAT_DATETIME,
  DATETIME_FORMAT_DATE,
} from '../../../../../utils/helpers/CommonHelper.jsx';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import dayjs from 'dayjs';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import Form from '../../../../antd/form/Form/Form.jsx';

const ReservationForm = ({
  roomsOfDepartment,
  roomsOther,
  teachers,
  subjects,
  form,
  isRoomReservationsAdmin,
  collidedSubjects,
  timeConflicts,
  conflictsChecked,
  onFinish,
  initialValues,
  currentReservationUserId,
}) => {
  const { t } = useTranslation();
  const { getFieldValue } = form;
  const [allDay, setAllDay] = useState(getFieldValue('all_day'));

  const disabledDate = (current) => {
    return current.isBefore(dayjs(), 'day'); // Disable all dates before today
  };

  // Improved validator for the "date_to" field
  const validateTo = async (_rule, value) => {
    const fromVal = getFieldValue('date_from');
    const toVal = value;
    if (!fromVal || !toVal) return Promise.resolve();

    const dateFrom = dayjs(fromVal);
    const dateTo = dayjs(toVal);
    if (!dateFrom.isValid() || !dateTo.isValid()) return Promise.resolve();

    if (getFieldValue('all_day')) {
      // For all-day, date_to must be same day or after date_from (ignore time)
      if (dateTo.startOf('day').isBefore(dateFrom.startOf('day'))) {
        return Promise.reject(
          t('Dátum konca nesmie byť skôr ako dátum začiatku'),
        );
      }
      // Max duration: 5 calendar days inclusive
      const inclusiveDays =
        dateTo.startOf('day').diff(dateFrom.startOf('day'), 'day') + 1;
      if (inclusiveDays > 5) {
        return Promise.reject(t('Maximálna dĺžka rezervácie je 5 dní'));
      }
    } else {
      // For time-specific, end must be strictly after start
      if (!dateTo.isAfter(dateFrom)) {
        return Promise.reject(t('Koniec musí byť neskôr ako začiatok'));
      }
      // Max duration: 5 days (120 hours)
      const maxMs = 5 * 24 * 60 * 60 * 1000;
      if (dateTo.valueOf() - dateFrom.valueOf() > maxMs) {
        return Promise.reject(t('Maximálna dĺžka rezervácie je 5 dní'));
      }
    }
    return Promise.resolve();
  };

  // Validator for date_from: today cannot be before current time (when not all_day)
  const validateFrom = async (_rule, value) => {
    if (!value) return Promise.resolve();
    if (getFieldValue('all_day')) return Promise.resolve();
    const dateFrom = dayjs(value);
    if (!dateFrom.isValid()) return Promise.resolve();

    const now = dayjs();
    // Disallow selecting a start in the past relative to now
    if (dateFrom.isBefore(now)) {
      return Promise.reject(t('Začiatok nesmie byť v minulosti'));
    }
    return Promise.resolve();
  };

  // Validate date_from and date_to immediately at mount
  useEffect(() => {
    setTimeout(() => form.validateFields(['date_to']), 0);
  }, []);

  useEffect(() => {
    if (
      currentReservationUserId &&
      !form.getFieldValue('created_for_id')
    ) {
      form.setFieldValue('created_for_id', currentReservationUserId);
    }
  }, [currentReservationUserId, form]);

   // Build room options dynamically; hide "Katedrové" when empty
   // "Ostatné" is filtered in parent to exclude other department rooms in create/edit
   const roomOptions = [];
   if (Array.isArray(roomsOfDepartment) && roomsOfDepartment.length > 0) {
     roomOptions.push({
       label: <span>{t('Katedrové')}</span>,
       title: 'katedrove',
       options: roomsOfDepartment,
     });
   }
   if (Array.isArray(roomsOther) && roomsOther.length > 0) {
     roomOptions.push({
       label: <span>{t('Ostatné')}</span>,
       title: 'ostatne',
       options: roomsOther,
     });
   }

  return (
    <>
      <Form form={form} onFinish={onFinish} initialValues={initialValues}>
        <CollapseBlock>
          <Row>
            <Col xs={24} md={6}>
              <FormSelect
                label={t('Miestnosť')}
                name={'room_id'}
                options={roomOptions}
                rules={[required(t)]}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={6}>
              <FormSelect
                label={t('Rezervované pre')}
                name={'created_for_id'}
                options={teachers}
                rules={[required(t)]}
                initialValue={initialValues?.created_for_id}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={6}>
              <FormTextField
                label={t('Dôvod rezervácie')}
                name={'reason'}
                rules={[required(t)]}
              />
            </Col>
          </Row>
          <Row>
            <FormSwitch
              name="all_day"
              label={t('Celý deň')}
              checked={allDay}
              onChange={(checked) => {
                setAllDay(checked);
                // ensure form value is in sync, then validate date_to only (errors appear on date_to)
                form.setFieldsValue({ all_day: checked });
                form.validateFields(['date_to']);
              }}
            />
          </Row>
          <Row style={{ width: '100%' }}>
            <Col xs={24} md={3}>
              <FormDate
                label={t('Začiatok rezervácie')}
                name={'date_from'}
                rules={[required(t), { validator: validateFrom }]}
                showTime={!allDay}
                disabledDate={disabledDate}
                format={
                  allDay ? DATETIME_FORMAT_DATE : DATETIME_FORMAT_DATETIME
                }
                dependencies={['date_to', 'all_day']}
                onChange={() => form.validateFields(['date_to'])}
              />
            </Col>
            <Col xs={24} md={4}>
              <FormDate
                label={t('Koniec rezervácie')}
                name={'date_to'}
                rules={[{ validator: validateTo }, required(t)]}
                showTime={!allDay}
                disabledDate={disabledDate}
                format={
                  allDay ? DATETIME_FORMAT_DATE : DATETIME_FORMAT_DATETIME
                }
                dependencies={['date_from', 'all_day']}
              />
            </Col>
          </Row>

          {/* Normalize time conflicts to an array and render accordingly */}
          {(() => {
            const conflictsArray = Array.isArray(timeConflicts)
              ? timeConflicts
              : timeConflicts && Array.isArray(timeConflicts.conflicts)
                ? timeConflicts.conflicts
                : timeConflicts
                  ? [timeConflicts]
                  : [];

            if (conflictsChecked && conflictsArray.length > 0) {
              return (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: '10px' }}
                  title={t(
                    'Zvolený termín koliduje s jednou alebo viacerými rezerváciami:',
                  )}
                  description={
                    <div>
                      <ul>
                        {conflictsArray.map((conflict) => (
                          <li
                            key={`${conflict.id ?? conflict.date_from}-${conflict.date_from}-${conflict.date_to}`}
                          >
                            <b>{conflict.orderedByName || ''} </b>(
                            {dayjs(conflict.date_from).format(
                              DATETIME_FORMAT_DATETIME,
                            )}
                            {' - '}
                            {dayjs(conflict.date_to).format(
                              DATETIME_FORMAT_DATETIME,
                            )}
                            )
                            <em>
                              {conflict.reason ? ` - ${conflict.reason}` : ''}
                            </em>
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                />
              );
            }

            if (conflictsChecked && conflictsArray.length === 0) {
              return (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: '10px' }}
                  title={t('Žiadne konflikty v termínoch')}
                  description={t('Miestnosť je v zadanom termíne voľná.')}
                />
              );
            }
            return null;
          })()}

          <Col xs={24} md={6}>
            <FormSelect
              label={t('Predmet')}
              name={'subject_code'}
              options={subjects}
              help={
                <div style={{ marginBottom: '10px' }}>
                  {t(
                    'Ak vyberiete predmet, aplikácia zistí ostatné skúšky v rovnaký termín pre rovnaký ročník a študijný program a zobrazí kolízie.',
                  )}
                </div>
              }
            />
          </Col>

          {getFieldValue('subject_code') !== undefined &&
            (Array.isArray(collidedSubjects) && collidedSubjects.length > 0 ? (
              <Alert
                type="warning"
                style={{ marginBottom: '10px' }}
                showIcon
                title={t(
                  'Zoznam rezervácií týkajúcich sa predmetov pre rovnaký ročník a študijný program:',
                )}
                description={
                  <ul>
                    {collidedSubjects.map((collision) => {
                      return (
                        <li
                          key={`${collision.subject_code}-${collision.date_from}-${collision.date_to}`}
                        >
                          <strong>{collision.name}</strong>,{' '}
                          {collision.displayname} (
                          {dayjs(collision.date_from).format(
                            DATETIME_FORMAT_DATETIME,
                          )}
                          -
                          {dayjs(collision.date_to).format(
                            DATETIME_FORMAT_DATETIME,
                          )}
                          ) - <em>{collision.reason}</em>
                        </li>
                      );
                    })}
                  </ul>
                }
              />
            ) : (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: '10px' }}
                title={t('Žiadne kolízie s inými predmetmi')}
                description={t(
                  'Neboli nájdené žiadne rezervácie s predmetmi pre rovnaký ročník a študijný program.',
                )}
              />
            ))}

          {isRoomReservationsAdmin ? (
            <FormSwitch name="auto_accept" label={t('Automaticky schváliť')} />
          ) : (
            ''
          )}
          <FormButtonsContainer>
            <FormButton submitOnClick={true} submitKey={'reservations'}>
              {t('Uložiť')}
            </FormButton>
          </FormButtonsContainer>
        </CollapseBlock>
      </Form>
    </>
  );
};

ReservationForm.propTypes = {
  props: PropTypes.any,
  currentReservationUserId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

export default ReservationForm;
